/**
 * RouteError — branded fallback wired into __root.tsx as the router's
 * `errorComponent`. Renders when a route loader throws, a render
 * blows up below the boundary, or a TanStack `notFound()` propagates
 * with no closer handler. Replaces TanStack Router's default
 * unstyled fallback so the experience matches the rest of the site.
 *
 * Content design (Task 3.20):
 *   - Headline copy that admits something went wrong without leaking
 *     internals. We never show a stack trace; we never show the raw
 *     error object. The error's `.message` is shown only if it looks
 *     safe (short, no "TypeError", no "Cannot read…"). Otherwise we
 *     fall back to a generic apology.
 *   - Correlation reference: short uppercase token derived from
 *     `crypto.randomUUID()` so support tickets carry a stable handle
 *     the user can quote. We surface it verbatim in the
 *     "Contact support" mailto so it makes it into the user's mail
 *     client even without manual copy/paste.
 *   - Two CTAs: "Try again" calls `window.location.reload()` (the
 *     widest possible reset — invalidates query cache, re-runs all
 *     loaders, clears component state) and "Contact support" opens
 *     a mailto with the reference pre-filled.
 *
 * Accessibility: section has a single `<h1>`, the reference block is
 * marked with `role="status"` so AT users hear the reference change
 * but not the surrounding decorative copy.
 */

import { useMemo } from 'react';
import { Link } from '@tanstack/react-router';
import { MarketingLayout } from '../layouts/marketing-layout';

export interface RouteErrorProps {
  /** The error passed in by TanStack Router's `errorComponent` slot.
   *  We tolerate `unknown` (loaders may throw anything). */
  error?: unknown;
  /** Optional override for the correlation reference. Tests pass a
   *  fixed value so assertions are deterministic. Real renders pull
   *  a fresh randomUUID on every mount. */
  correlationId?: string;
}

/**
 * Coerce an unknown thrown value into a single short, safe string.
 * Goals:
 *   - never leak a stack trace
 *   - never echo a TypeError/ReferenceError verbatim (those usually
 *     contain identifiers from minified bundles, which leak nothing
 *     useful to the user and a fair amount of intel to attackers)
 *   - bound the length so the layout doesn't blow up on a 4 KB string
 */
function safeMessage(error: unknown): string | null {
  if (!error) return null;
  const raw =
    typeof error === 'string'
      ? error
      : error instanceof Error
        ? error.message
        : (() => {
            try {
              return String((error as { message?: unknown }).message ?? '');
            } catch {
              return '';
            }
          })();
  const trimmed = raw.trim();
  if (!trimmed) return null;

  // Reject anything that looks like a runtime/programmer error —
  // these are useless to the user and unsafe to surface.
  const banned = /\b(TypeError|ReferenceError|SyntaxError|RangeError|Cannot read|undefined is not|stack:)/i;
  if (banned.test(trimmed)) return null;

  // Hard cap. Anything longer is almost certainly noise.
  return trimmed.length > 240 ? null : trimmed;
}

/**
 * Generate a short, uppercase, dash-formatted correlation reference
 * suitable for the user to read aloud to support. Falls back to a
 * timestamp-based handle when `crypto.randomUUID` is unavailable
 * (very old browsers, some non-secure contexts).
 */
function generateCorrelationId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      const uuid = crypto.randomUUID();
      // ABCDEF12-3456-7890 — easy to read over the phone, plenty of
      // entropy for a debugging handle.
      return `${uuid.slice(0, 8)}-${uuid.slice(9, 13)}-${uuid.slice(14, 18)}`.toUpperCase();
    }
  } catch {
    // crypto can throw in non-secure contexts on some browsers.
  }
  const ts = Date.now().toString(36).toUpperCase();
  const rand = Math.floor(Math.random() * 0xffff).toString(16).toUpperCase().padStart(4, '0');
  return `${ts}-${rand}`;
}

export function RouteError({ error, correlationId }: RouteErrorProps = {}) {
  // Memoise so the reference is stable across re-renders of this
  // boundary — otherwise the user sees a new handle every paint.
  const reference = useMemo(
    () => correlationId ?? generateCorrelationId(),
    [correlationId],
  );

  const message = safeMessage(error);

  return (
    <MarketingLayout>
      <section
        aria-labelledby="route-error-title"
        style={{
          maxWidth: 720,
          margin: '0 auto',
          padding: '96px 48px 120px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--hairline)',
            borderRadius: 'var(--r-xl)',
            padding: '56px 48px 48px',
            width: '100%',
            maxWidth: 600,
          }}
        >
          <p className="eyebrow" style={{ marginBottom: 24 }}>
            Something went wrong
          </p>

          <h1
            id="route-error-title"
            className="serif"
            style={{
              fontSize: 'clamp(36px, 6vw, 60px)',
              fontStyle: 'italic',
              fontWeight: 300,
              color: 'var(--ink)',
              lineHeight: 1.1,
              letterSpacing: '-0.025em',
              margin: '0 0 24px',
            }}
          >
            We hit an unexpected error
          </h1>

          <p
            style={{
              fontSize: 17,
              color: 'var(--ink-3)',
              lineHeight: 1.6,
              margin: '0 0 24px',
            }}
          >
            Your data is safe. Nothing has been changed. Please try
            again, or contact us with the reference below and we&rsquo;ll
            look into it.
          </p>

          {message && (
            <p
              data-testid="error-message"
              style={{
                fontSize: 14,
                color: 'var(--ink-3)',
                fontStyle: 'italic',
                margin: '0 0 24px',
              }}
            >
              &ldquo;{message}&rdquo;
            </p>
          )}

          {/* Correlation reference — read aloud to support, copy/paste
              from screenshots. role="status" lets AT users hear the
              handle without re-reading the rest of the card. */}
          <div
            role="status"
            data-testid="correlation-reference"
            style={{
              border: '1px solid var(--hairline)',
              borderRadius: 8,
              padding: '12px 16px',
              margin: '0 auto 32px',
              maxWidth: 360,
              background: 'var(--paper-2, var(--bg-2))',
            }}
          >
            <div
              className="eyebrow"
              style={{ fontSize: 11, marginBottom: 4 }}
            >
              Reference
            </div>
            <div
              className="mono"
              style={{ fontSize: 14, color: 'var(--ink-2)', wordBreak: 'break-all' }}
            >
              {reference}
            </div>
          </div>

          <div
            style={{
              borderTop: '1px solid var(--hairline)',
              marginBottom: 32,
            }}
          />

          <div
            style={{
              display: 'flex',
              gap: 16,
              flexWrap: 'wrap',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <button
              type="button"
              data-testid="try-again-cta"
              onClick={() => {
                // Hard reload is the widest possible reset: it
                // clears the query cache, re-runs all loaders, and
                // unmounts every component that might be holding
                // a stale closure over the failed value. Cheap.
                if (typeof window !== 'undefined') window.location.reload();
              }}
              style={{
                background: 'var(--accent-400)',
                color: 'var(--primary-900)',
                padding: '11px 22px',
                borderRadius: 10,
                fontSize: 15,
                fontWeight: 600,
                border: 'none',
                cursor: 'pointer',
                fontFamily: 'var(--sans)',
              }}
            >
              Try again
            </button>

            <span
              data-testid="contact-support-cta"
              style={{
                fontSize: 15,
                color: 'var(--ink-3)',
              }}
            >
              If it keeps happening, contact your administrator with the reference above.
            </span>

            <Link
              to="/"
              style={{
                fontSize: 15,
                color: 'var(--primary-600)',
                textDecoration: 'none',
                borderBottom: '1px solid rgba(31,77,69,0.25)',
                paddingBottom: 1,
              }}
            >
              Return home
            </Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
